import { memo, useMemo, type FC } from "react";
import { X } from "lucide-react";
import StyledDropdown from "src/components/reusable/StyledDropdown";
import StyledTextInput from "src/components/reusable/StyledTextInput";
import CampaignStepCard from "src/layouts/Engagements/Campaigns/shared/CampaignStepCard";
import CampaignTextField from "src/layouts/Engagements/Campaigns/shared/CampaignTextField";
import CampaignFormField from "src/layouts/Engagements/Campaigns/shared/CampaignFormField";
import { useCreateListLabel } from "../useCreateListLabel";

const FIELD_IDS = {
  listName: "create-list-name",
  description: "create-list-description",
  availableTags: "create-list-available-tags",
  customTag: "create-list-custom-tag",
};

interface NameTagsStepProps {
  listName: string;
  description: string;
  tags: string[];
  availableTags: string[];
  onListNameChange: (_value: string) => void;
  onDescriptionChange: (_value: string) => void;
  onAddTag: (_tag: string) => void;
  onRemoveTag: (_tag: string) => void;
  labelKey?: string;
}

const NameTagsStep: FC<NameTagsStepProps> = ({
  listName,
  description,
  tags,
  availableTags,
  onListNameChange,
  onDescriptionChange,
  onAddTag,
  onRemoveTag,
  labelKey,
}) => {
  const label = useCreateListLabel(labelKey);
  const descriptionHelp = label("details.descriptionHelp");

  const availableTagByKey = useMemo(
    () => new Map(availableTags.map(tag => [tag.toLowerCase(), tag])),
    [availableTags]
  );

  const tagOptions = useMemo(
    () => availableTags.map(tag => ({ value: tag, label: tag })),
    [availableTags]
  );

  const selectedAvailableTags = useMemo(
    () => tags.filter(tag => availableTagByKey.has(tag.toLowerCase())),
    [tags, availableTagByKey]
  );

  const customTags = useMemo(
    () => tags.filter(tag => !availableTagByKey.has(tag.toLowerCase())),
    [tags, availableTagByKey]
  );

  const syncTags = (current: string[], next: string[]) => {
    next
      .filter(tag => !current.includes(tag))
      .forEach(tag => onAddTag(availableTagByKey.get(tag.toLowerCase()) ?? tag));
    current.filter(tag => !next.includes(tag)).forEach(onRemoveTag);
  };

  const handleAvailableTagsChange = (values: string[]) => syncTags(selectedAvailableTags, values);

  const handleCustomTagsChange = (values: string[]) => syncTags(customTags, values);

  return (
    <CampaignStepCard title={label("details.title")} subtitle={label("details.subtitle")}>
      <CampaignTextField
        id={FIELD_IDS.listName}
        label={label("details.listName")}
        value={listName}
        onChange={onListNameChange}
        placeholder={label("details.listNamePlaceholder")}
        required
      />

      <CampaignFormField
        label={label("details.description")}
        htmlFor={FIELD_IDS.description}
        hint={label("details.optional")}
      >
        <StyledTextInput
          id={FIELD_IDS.description}
          value={description}
          onChange={event => onDescriptionChange(event.target.value)}
          placeholder={label("details.descriptionPlaceholder")}
          className="campaign-flow__input"
        />
        {descriptionHelp && <span className="create-list__helper">{descriptionHelp}</span>}
      </CampaignFormField>

      <div className="create-list__tags">
        <span className="campaign-flow-field__label">{label("details.tags")}</span>

        <label htmlFor={FIELD_IDS.availableTags} className="create-list__sublabel">
          {label("details.availableTags")}
        </label>
        <StyledDropdown
          id={FIELD_IDS.availableTags}
          mode="multiple"
          maxTagCount="responsive"
          value={selectedAvailableTags}
          options={tagOptions}
          onChange={handleAvailableTagsChange}
          placeholder={label("details.selectTag")}
          optionFilterProp="label"
          notFoundContent={label("details.noTags")}
          className="campaign-flow__select create-list__tags-select"
          popupClassName="campaign-flow__popup create-list__tags-popup"
          menuItemSelectedIcon={null}
        />

        <label htmlFor={FIELD_IDS.customTag} className="create-list__sublabel">
          {label("details.customTag")}
        </label>
        <StyledDropdown
          id={FIELD_IDS.customTag}
          mode="tags"
          open={false}
          maxTagCount="responsive"
          value={customTags}
          onChange={handleCustomTagsChange}
          placeholder={label("details.customTagPlaceholder")}
          className="campaign-flow__select create-list__tags-select create-list__tags-select--input"
        />
        <span className="create-list__helper">{label("details.customTagHelp")}</span>

        {tags.length > 0 && (
          <div className="create-list__chips">
            {tags.map(tag => (
              <span key={tag} className="create-list__chip">
                {tag}
                <button
                  type="button"
                  className="create-list__chip-remove"
                  aria-label={label("details.removeTag", { tag })}
                  onClick={() => onRemoveTag(tag)}
                >
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>
    </CampaignStepCard>
  );
};

export default memo(NameTagsStep);
